import React from 'react';
import type { App } from '../../hooks/use-app';
import UniversalMediaUploaderStatus from '../../components/universal-media-uploader-status';
import { formatMediaDateTitle } from '../media/metadata-date';
import type { Image } from './media-props';
import type { MediaType } from './media-field-type';
import CaptionedThumbnail from './captioned-thumbnail';
import Thumbnail from './thumbnail';
import AudioItemContent from './audio-item-content';
import { getAttachmentUploader, getAttachmentStatusChecker } from './helpers/attachment-upload-helpers';
import AttachmentItem from './attachment-item';
import SignaturePreviewItem from './signature-preview-item';
import SignatureUploaderPreview from './media/signature-uploader-preview';

/**
 * HydratedMediaItem extends the raw Image with an onOpenViewer callback.
 * MediaField hydrates each item before passing it to renderItem.
 */
export interface HydratedMediaItem extends Image {
  onOpenViewer?: () => void;
}

export interface MediaStatusResult {
  ok: boolean;
}

/**
 * Dependencies injected into getRenderItem by the useMediaElement hook.
 */
export interface RenderItemDeps {
  statusChecker?: (mediaID: string) => Promise<MediaStatusResult>;
  editing?: boolean;
  disabled?: boolean;
  attachmentsUrl?: string;
  token?: string;
  onRemove?: (mediaID: string) => void;
  isMultiFileEnabled?: boolean;
  onExistingAttachmentClick?: (mediaID: string) => void;
}

export interface FileMetadata {
  meta: { name: string; size: number };
  file: File;
}

export interface UploadParamsResult {
  method: string;
  body: File | FormData;
  url: string;
  headers: Record<string, string>;
  attachmentId?: string;
}

export interface UploadParamsDeps {
  token: string;
  endpoint: string;
  fieldName: string;
  attachmentsUrl: string;
}

export interface MediaTypeConfigEntry {
  fileTypes: string;
  buttonText: string;
  captionEnabled?: boolean;
  maxItems?: number;
  directCommit?: boolean;
  getUploader: (ctx: App) => (file: File, progress?: (event: ProgressEvent) => void) => Promise<unknown>;
  getThumbnailURL: (ctx: App) => (item: Image) => string;
  getRenderItem: (deps: RenderItemDeps) => (item: HydratedMediaItem) => React.ReactNode;
  getUploadParams: (deps: UploadParamsDeps) => (fileMetadata: FileMetadata) => Promise<UploadParamsResult>;
  getTrackUploader?: (ctx: App) => (
    mediaID: string, file: File, progress: (event: ProgressEvent) => void,
  ) => Promise<unknown>;
  getStatusChecker?: (ctx: App, editing: boolean) => ((mediaID: string) => Promise<MediaStatusResult>) | undefined;
  /** When true, the render item manages its own viewer — modalProps is suppressed. */
  selfContainedViewer?: boolean;
  wrapValue?: (value: unknown) => {
    items: Image[];
    addItem: (id: string, name?: string) => void;
    removeItem: (mediaID: string) => void;
  };
}

export type { MediaType } from './media-field-type';

/**
 * Plural labels for media types, used for UI display (buttons, headers, etc.)
 */
export const MEDIA_TYPE_PLURAL: Record<MediaType, string> = {
  photo: 'Photos',
  video: 'Videos',
  audio: 'Audio',
  sketch: 'Sketches',
  signature: 'Signatures',
  attachment: 'Attachments',
};

function getAudioStatusChecker(ctx: App, editing: boolean) {
  if (!editing) return undefined;

  return (mediaID: string): Promise<MediaStatusResult> => new Promise((resolve, reject) => {
    ctx.dataSource.getAudio(mediaID, (err: unknown, result: { uploaded: boolean }) => {
      if (err) {
        reject(err instanceof Error ? err : new Error('Audio status check failed'));
      } else {
        resolve({ ok: result.uploaded });
      }
    });
  });
}

/**
 * Shared upload-params factory for media types that POST FormData
 * (photo, video, audio, signature, sketch).
 */
function formDataUploadParams({ token, endpoint, fieldName }: UploadParamsDeps) {
  return async (fileMetadata: FileMetadata): Promise<UploadParamsResult> => {
    const accessKey = crypto.randomUUID();
    const formData = new FormData();
    formData.append(`${fieldName}[access_key]`, accessKey);
    formData.append(`${fieldName}[file]`, fileMetadata.file, fileMetadata.meta.name);

    return {
      method: 'POST',
      body: formData,
      url: endpoint,
      headers: {
        'X-ApiToken': token,
        Accept: 'application/json',
      },
      attachmentId: accessKey,
    };
  };
}

/**
 * Attachment-specific upload-params factory. Creates a presigned URL via
 * the attachments API, then returns PUT params for direct S3 upload.
 */
function attachmentUploadParams({ token, attachmentsUrl }: UploadParamsDeps) {
  return async (fileMetadata: FileMetadata): Promise<UploadParamsResult> => {
    const fileName = fileMetadata.meta.name.replaceAll('"', "'");
    const requestBody = {
      name: fileName,
      owners: [{ type: 'record' }],
      file_size: fileMetadata.meta.size,
      metadata: { filename: fileName },
    };

    const response = await fetch(attachmentsUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-ApiToken': token },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) throw new Error('Upload failed. Please try again.');

    const presigned = await response.json();

    return {
      method: 'PUT',
      body: fileMetadata.file,
      url: presigned.url as string,
      headers: (presigned.headers ?? {}) as Record<string, string>,
      attachmentId: presigned.id as string,
    };
  };
}

// Configs added incrementally — video, audio, signature filled in during migration.
export const mediaTypeConfig: Record<MediaType, MediaTypeConfigEntry> = {
  photo: {
    fileTypes: 'image/*',
    buttonText: 'Add Photos',
    captionEnabled: true,
    getUploader: (ctx) => (file, progress) => ctx.api.photos.create(file, { access_key: null }, progress),
    getThumbnailURL: (ctx) => (item) => ctx.dataSource?.source?.getPhotoThumbnailURL(item?.mediaID) ?? '',
    getUploadParams: formDataUploadParams,
    getRenderItem: ({ editing, disabled, onRemove }) => (item) => (
      <CaptionedThumbnail
        url={item.thumbnailURL}
        mediaID={item.mediaID}
        caption={item.caption}
        onCaptionChange={item.onCaptionChange}
        selectMedia={item.onOpenViewer}
        onClick={item.onOpenViewer}
        editing={editing}
        disabled={disabled}
        fileSize={item.fileSize}
        fileName={item.fileName}
        createdAt={item.createdAt}
        onRemove={onRemove ? () => onRemove(item.mediaID) : undefined}
        imgStyles={{ maxWidth: 150, borderRadius: 5 }}
        fileType="photo"
      />
    ),
  },
  video: {
    fileTypes: 'video/*',
    buttonText: 'Select Video',
    captionEnabled: true,
    getUploader: (ctx) => (file, progress) => ctx.dataSource.source.createVideo(null, file, progress),
    getThumbnailURL: (ctx) => (item) => ctx.dataSource?.source?.getVideoThumbnailURL(item?.mediaID) ?? '',
    getUploadParams: formDataUploadParams,
    getRenderItem: ({ editing, disabled, onRemove }) => (item) => (
      <CaptionedThumbnail
        url={item.thumbnailURL}
        mediaID={item.mediaID}
        caption={item.caption}
        onCaptionChange={item.onCaptionChange}
        selectMedia={item.onOpenViewer}
        onClick={item.onOpenViewer}
        editing={editing}
        disabled={disabled}
        fileSize={item.fileSize}
        fileName={item.fileName}
        createdAt={item.createdAt}
        onRemove={onRemove ? () => onRemove(item.mediaID) : undefined}
        imgStyles={{ maxWidth: 150, borderRadius: 5 }}
        fileType="video"
      />
    ),
    getTrackUploader: (ctx) => (mediaID, file, progress) => (
      ctx.dataSource.source.saveVideoTrack(mediaID, file, progress)
    ),
  },
  audio: {
    fileTypes: 'audio/*',
    buttonText: 'Select Audio',
    captionEnabled: true,
    getUploader: (ctx) => (file, progress) => ctx.dataSource.source.createAudio(null, file, progress),
    getThumbnailURL: () => () => '',
    getUploadParams: formDataUploadParams,
    getRenderItem: ({
      statusChecker, editing, disabled, onRemove,
    }) => (item) => (
      <CaptionedThumbnail
        url=""
        mediaID={item.mediaID}
        caption={item.caption}
        onCaptionChange={item.onCaptionChange}
        onClick={item.onOpenViewer}
        editing={editing}
        disabled={disabled}
        fileSize={item.fileSize}
        fileName={item.fileName}
        createdAt={item.createdAt}
        onRemove={onRemove ? () => onRemove(item.mediaID) : undefined}
        fileType="audio"
      >
        <AudioItemContent item={item} statusChecker={statusChecker} />
      </CaptionedThumbnail>
    ),
    getTrackUploader: (ctx) => (mediaID, file, progress) => (
      ctx.dataSource.source.saveAudioTrack(mediaID, file, progress)
    ),
    getStatusChecker: (ctx, editing) => getAudioStatusChecker(ctx, editing),
  },
  sketch: {
    fileTypes: 'image/*',
    buttonText: 'Add Sketches',
    getUploader: (ctx) => (file, progress) => ctx.dataSource.source.createSketch(null, file, progress),
    getThumbnailURL: (ctx) => (item) => ctx.dataSource?.source?.getSketchThumbnailURL(item?.mediaID) ?? '',
    getUploadParams: formDataUploadParams,
    getRenderItem: ({
      editing, disabled, onRemove, isMultiFileEnabled,
    }) => (item) => (
      isMultiFileEnabled
        ? (
          <UniversalMediaUploaderStatus
            name={item.fileName ?? formatMediaDateTitle(item.createdAt)}
            size={item.fileSize}
            thumbnailURL={item.thumbnailURL}
            onClose={editing && !disabled && onRemove ? () => onRemove(item.mediaID) : undefined}
            onClick={item.onOpenViewer}
            avatarSize={editing ? 'default' : 'large'}
            fileType="sketch"
          />
        )
        : (
          <Thumbnail
            url={item.thumbnailURL}
            selectMedia={item.onOpenViewer}
            imgStyles={{ maxWidth: 150, borderRadius: 5 }}
          />
        )
    ),
  },
  signature: {
    fileTypes: 'image/*',
    buttonText: 'Select Signature',
    maxItems: 1,
    directCommit: true,
    selfContainedViewer: true,
    getUploader: (ctx) => (file, progress) => ctx.dataSource.source.createSignature(null, file, progress),
    getThumbnailURL: (ctx) => (item) => ctx.dataSource?.source?.getSignatureThumbnailURL(item?.mediaID) ?? '',
    getUploadParams: formDataUploadParams,
    getRenderItem: ({
      editing, disabled, onRemove, isMultiFileEnabled,
    }) => (item) => (
      isMultiFileEnabled
        ? (
          <SignatureUploaderPreview
            mediaID={item.mediaID}
            name={item.fileName ?? formatMediaDateTitle(item.createdAt)}
            size={item.fileSize}
            thumbnailURL={item.thumbnailURL}
            onRemove={editing && !disabled && onRemove ? () => onRemove(item.mediaID) : undefined}
            avatarSize={editing ? 'default' : 'large'}
          />
        )
        : (
          <SignaturePreviewItem
            mediaID={item.mediaID}
            thumbnailURL={item.thumbnailURL}
          />
        )
    ),
    wrapValue: (value: unknown) => {
      const sig = value as { id: string; isEmpty: boolean; clear: () => void; timestamp: Date };
      const items: Image[] = sig.isEmpty ? [] : [{
        mediaID: sig.id, caption: '', url: '', thumbnailURL: '', onDelete: () => {}, onCaptionChange: () => {},
      }];
      return {
        items,
        addItem: (id: string) => {
          sig.id = id;
          sig.timestamp = new Date();
          if (items.length === 0) {
            items.push({
              mediaID: id, caption: '', url: '', thumbnailURL: '', onDelete: () => {}, onCaptionChange: () => {},
            });
          } else {
            items[0].mediaID = id;
          }
        },
        removeItem: () => {
          sig.clear();
          items.length = 0;
        },
      };
    },
  },

  attachment: {
    fileTypes: '',
    buttonText: 'Select File',
    selfContainedViewer: true,
    getUploader: (ctx) => getAttachmentUploader(ctx),
    getThumbnailURL: () => () => '',
    getUploadParams: attachmentUploadParams,
    getRenderItem: (deps) => (item) => (
      deps.isMultiFileEnabled
        ? (
          <UniversalMediaUploaderStatus
            name={item.fileName ?? formatMediaDateTitle(item.createdAt)}
            size={item.fileSize}
            avatarSize={deps.editing ? 'default' : 'large'}
            fileType="attachment"
            onClose={deps.editing && !deps.disabled && deps.onRemove ? () => deps.onRemove!(item.mediaID) : undefined}
            onClick={deps.onExistingAttachmentClick ? () => deps.onExistingAttachmentClick!(item.mediaID) : undefined}
          />
        )
        : (
          <AttachmentItem
            item={item}
            statusChecker={deps.statusChecker}
            editing={deps.editing}
            attachmentsUrl={deps.attachmentsUrl}
            token={deps.token}
          />
        )
    ),
    getStatusChecker: (ctx, editing) => getAttachmentStatusChecker(ctx, editing),
  },
};

type MediaElementLike = {
  isPhotoElement?: boolean;
  isVideoElement?: boolean;
  isAudioElement?: boolean;
  isSketchElement?: boolean;
  isSignatureElement?: boolean;
  isAttachmentElement?: boolean;
};

/**
 * Maps element type flags to config keys.
 * Returns undefined for unsupported element types.
 */
export function resolveMediaType(element: MediaElementLike): MediaType | undefined {
  if (element.isPhotoElement) return 'photo';
  if (element.isVideoElement) return 'video';
  if (element.isAudioElement) return 'audio';
  if (element.isSketchElement) return 'sketch';
  if (element.isSignatureElement) return 'signature';
  if (element.isAttachmentElement) return 'attachment';
  return undefined;
}

const mediaAddEvents: Partial<Record<MediaType, string>> = {
  photo: 'add-photo',
  video: 'add-video',
  audio: 'add-audio',
  attachment: 'add-attachment',
  sketch: 'add-sketch',
};

const mediaRemoveEvents: Partial<Record<MediaType, string>> = {
  photo: 'remove-photo',
  video: 'remove-video',
  audio: 'remove-audio',
  sketch: 'remove-sketch',
};

export function resolveMediaAddEvent(element: MediaElementLike): string | null {
  const mediaType = resolveMediaType(element);
  return mediaType ? mediaAddEvents[mediaType] ?? null : null;
}

export function resolveMediaRemoveEvent(element: MediaElementLike): string | null {
  const mediaType = resolveMediaType(element);
  return mediaType ? mediaRemoveEvents[mediaType] ?? null : null;
}
